import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Star, Calendar } from 'lucide-react';
import { Media, FavoriteItem } from '../types';
import { getImageUrl } from '../api/tmdb';
import { useFavorites } from '../hooks/useFavorites';

interface MovieCardProps {
  item: Media;
  showType?: boolean;
}

const MovieCard: React.FC<MovieCardProps> = ({ item, showType = true }) => {
  const { favorites, addToFavorites, removeFromFavorites } = useFavorites();
  
  // Check if item is in favorites
  const isFavorite = favorites.some(
    fav => fav.id === item.id && fav.type === item.media_type
  );

  // Get title (movies have 'title', series have 'name')
  const title = 'title' in item ? item.title : item.name;
  
  // Get release date
  const releaseDate = 'release_date' in item ? item.release_date : item.first_air_date;
  
  // Get year from release date
  const year = releaseDate ? new Date(releaseDate).getFullYear() : 'N/A';

  // Handle favorite toggle
  const handleFavoriteToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const favoriteItem: FavoriteItem = {
      id: item.id,
      type: item.media_type!,
      title: title || 'Unknown Title',
      poster_path: item.poster_path,
      vote_average: item.vote_average,
      addedAt: Date.now(),
    };

    if (isFavorite) {
      removeFromFavorites(item.id, item.media_type!);
    } else {
      addToFavorites(favoriteItem);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -6 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="group relative bg-white dark:bg-dark-800 rounded-2xl overflow-hidden card-hover border border-gray-100 dark:border-dark-700"
    >
      <Link to={`/details/${item.media_type}/${item.id}`}>
        {/* Poster Image */}
        <div className="relative aspect-[2/3] overflow-hidden">
          <img
            src={getImageUrl(item.poster_path)}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 ease-out"
            loading="lazy"
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          {/* Favorite Button */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleFavoriteToggle}
            className={`absolute top-3 right-3 p-2 rounded-full transition-all duration-200 ${
              isFavorite
                ? 'bg-red-500 text-white shadow-lg'
                : 'bg-black/30 backdrop-blur-sm text-white hover:bg-red-500'
            }`}
            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Heart className={`h-4 w-4 ${isFavorite ? 'fill-current' : ''}`} />
          </motion.button>

          {/* Media Type Badge */}
          {showType && (
            <div className="absolute top-3 left-3 badge-modern">
              {item.media_type === 'movie' ? 'Movie' : 'TV Series'}
            </div>
          )}

          {/* Rating Badge */}
          <div className="absolute bottom-3 left-3 flex items-center space-x-1 px-2 py-1 bg-black/60 backdrop-blur-sm text-white text-xs rounded-lg">
            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
            <span className="font-medium">
              {typeof item.vote_average === 'number' && !isNaN(item.vote_average)
                ? item.vote_average.toFixed(1)
                : 'N/A'}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Title */}
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm line-clamp-2 mb-2 group-hover:text-gradient transition-colors duration-200">
            {title}
          </h3>

          {/* Meta Information */}
          <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
            <div className="flex items-center space-x-1">
              <Calendar className="h-3 w-3" />
              <span>{year}</span>
            </div>
            
            {/* Vote Count */}
            <span className="text-xs opacity-75">
              {typeof item.vote_count === 'number' && !isNaN(item.vote_count)
                ? `${item.vote_count.toLocaleString()} votes`
                : 'No votes'}
            </span>
          </div>

          {/* Overview */}
          {item.overview && (
            <p className="mt-3 text-xs text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed">
              {item.overview}
            </p>
          )}
        </div>
      </Link>
    </motion.div>
  );
};

export default MovieCard; 